import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { addLiveChatSocketListener, connectLiveChatSocket } from 'src/utils/liveChatSocket';
import { useToaster } from 'src/utils/toaster/toasterContext';

const getInitialPhoneNumberID = () =>
  new URLSearchParams(window.location.search).get('phone_number_id') ||
  new URLSearchParams(window.location.search).get('account_id') ||
  '';

export default function WhatsAppChatView() {
  const { showToast } = useToaster();
  const [accounts, setAccounts] = useState([]);
  const [selectedPhoneNumberID, setSelectedPhoneNumberID] = useState(getInitialPhoneNumberID());
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageCursor, setMessageCursor] = useState(null);
  const [hasEarlierMessages, setHasEarlierMessages] = useState(false);
  const [loadingEarlierMessages, setLoadingEarlierMessages] = useState(false);
  const [reply, setReply] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const selectedPhoneNumberIDRef = useRef(selectedPhoneNumberID);
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);
  const showToastRef = useRef(showToast);
  const messageRequestRef = useRef(0);

  const selectedAccount = useMemo(
    () =>
      accounts.find(
        (account) =>
          String(account.id) === String(selectedPhoneNumberID) || account.profile_id === selectedPhoneNumberID
      ) || null,
    [accounts, selectedPhoneNumberID]
  );
  const accountIsSyncing = selectedAccount?.is_syncing === true;

  const scrollToLatestMessage = useCallback(() => {
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ block: 'end' }));
  }, []);

  const appendMessage = useCallback(
    (nextMessage) => {
      if (!nextMessage) return;
      setMessages((current) => {
        if (nextMessage.id && current.some((message) => Number(message.id) === Number(nextMessage.id))) {
          return current;
        }
        return [...current, nextMessage];
      });
      scrollToLatestMessage();
    },
    [scrollToLatestMessage]
  );

  const updateConversationPreview = useCallback((incomingMessage) => {
    const conversationID = Number(incomingMessage?.conversation_id || 0);
    if (!conversationID) return false;
    const found = conversationsRef.current.some((conversation) => Number(conversation.id) === conversationID);
    const selectedConversationID = Number(selectedConversationRef.current?.id || 0);
    setConversations((current) =>
      current.map((conversation) => {
        if (Number(conversation.id) !== conversationID) return conversation;
        const mediaURL = String(incomingMessage.media_url || '').trim();
        const messageText =
          String(incomingMessage.message || '').trim() ||
          (mediaURL ? 'Visitor sent an attachment.' : conversation.last_question);
        const unreadIncrement =
          incomingMessage.sender_type === 'visitor' && selectedConversationID !== conversationID ? 1 : 0;
        return {
          ...conversation,
          last_question: messageText,
          updated_at: incomingMessage.created_at || conversation.updated_at,
          last_message_at: incomingMessage.created_at || conversation.last_message_at,
          unread_count: Math.max(0, Number(conversation.unread_count || 0) + unreadIncrement),
        };
      })
    );
    return found;
  }, []);

  const fetchAccounts = useCallback((silent = false) => {
    if (!silent) setLoadingAccounts(true);
    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=whatsapp`,
      successCallback: (data) => {
        const nextAccounts = (data?.accounts || []).filter(
          (account) => account.is_active !== false && account.is_enable !== false
        );
        setAccounts(nextAccounts);
        setSelectedPhoneNumberID((current) => current || String(nextAccounts[0]?.profile_id || ''));
        if (!silent) setLoadingAccounts(false);
      },
      failureCallback: (message) => {
        if (!silent) setLoadingAccounts(false);
        if (!silent) showToastRef.current(message || 'Failed to fetch WhatsApp accounts', 'e');
      },
    });
  }, []);

  const fetchConversations = useCallback(
    (phoneNumberID = selectedPhoneNumberID) => {
      if (!phoneNumberID) {
        setConversations([]);
        setSelectedConversation(null);
        setMessages([]);
        setMessageCursor(null);
        setHasEarlierMessages(false);
        return;
      }
      setLoadingConversations(true);
      const params = new URLSearchParams({ phone_number_id: phoneNumberID, page: '1', limit: '50' });
      HttpServices.Get({
        url: `${userModuleURL.whatsappConversations}?${params.toString()}`,
        successCallback: (data) => {
          const nextConversations = data?.conversations || [];
          setConversations(nextConversations);
          setSelectedConversation((current) => {
            const matchingConversation =
              current && nextConversations.find((conversation) => conversation.id === current.id);
            if (matchingConversation) return matchingConversation;
            return nextConversations[0] || null;
          });
          setLoadingConversations(false);
        },
        failureCallback: (message) => {
          setLoadingConversations(false);
          showToastRef.current(message || 'Failed to fetch WhatsApp conversations', 'e');
        },
      });
    },
    [selectedPhoneNumberID]
  );

  const fetchMessages = useCallback(
    (conversationID, cursor = null) => {
      if (!conversationID) {
        messageRequestRef.current += 1;
        setMessages([]);
        setMessageCursor(null);
        setHasEarlierMessages(false);
        return;
      }
      const requestID = cursor ? messageRequestRef.current : messageRequestRef.current + 1;
      if (!cursor) {
        messageRequestRef.current = requestID;
        setMessageCursor(null);
        setHasEarlierMessages(false);
      }
      if (cursor) {
        setLoadingEarlierMessages(true);
      } else {
        setLoadingMessages(true);
      }
      const params = new URLSearchParams({ limit: '50' });
      if (cursor?.beforeTime && cursor?.beforeID) {
        params.set('before_time', cursor.beforeTime);
        params.set('before_id', String(cursor.beforeID));
      }
      HttpServices.Get({
        url: `${userModuleURL.whatsappConversationMessages(conversationID)}?${params.toString()}`,
        successCallback: (data) => {
          if (requestID !== messageRequestRef.current) return;
          const nextMessages = Array.isArray(data) ? data : data?.messages || [];
          if (cursor) {
            setMessages((current) => {
              const existingIDs = new Set(current.map((message) => Number(message.id)));
              return [...nextMessages.filter((message) => !existingIDs.has(Number(message.id))), ...current];
            });
            setLoadingEarlierMessages(false);
          } else {
            setMessages(nextMessages);
            setLoadingMessages(false);
            setConversations((current) =>
              current.map((conversation) =>
                Number(conversation.id) === Number(conversationID) ? { ...conversation, unread_count: 0 } : conversation
              )
            );
            setSelectedConversation((current) =>
              Number(current?.id || 0) === Number(conversationID) ? { ...current, unread_count: 0 } : current
            );
            scrollToLatestMessage();
            window.dispatchEvent(new CustomEvent('brand2chat:social-unread-refresh', { detail: { service: 'whatsapp' } }));
          }
          setHasEarlierMessages(Array.isArray(data) ? false : data?.has_more === true);
          setMessageCursor(
            data?.has_more && data?.next_before_time && data?.next_before_id
              ? { beforeTime: data.next_before_time, beforeID: data.next_before_id }
              : null
          );
        },
        failureCallback: (message) => {
          if (requestID !== messageRequestRef.current) return;
          if (cursor) setLoadingEarlierMessages(false);
          else setLoadingMessages(false);
          showToastRef.current(message || 'Failed to fetch WhatsApp messages', 'e');
        },
      });
    },
    [scrollToLatestMessage]
  );

  const loadEarlierMessages = () => {
    if (!selectedConversation?.id || !messageCursor || loadingEarlierMessages) return;
    fetchMessages(selectedConversation.id, messageCursor);
  };

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (!accountIsSyncing) return undefined;
    const refreshTimer = window.setInterval(() => fetchAccounts(true), 4000);
    return () => window.clearInterval(refreshTimer);
  }, [accountIsSyncing, fetchAccounts]);

  useEffect(() => {
    if (accountIsSyncing) {
      messageRequestRef.current += 1;
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      setMessageCursor(null);
      setHasEarlierMessages(false);
      setLoadingEarlierMessages(false);
      return;
    }
    fetchConversations(selectedPhoneNumberID);
  }, [accountIsSyncing, fetchConversations, selectedPhoneNumberID]);

  useEffect(() => {
    fetchMessages(selectedConversation?.id);
  }, [fetchMessages, selectedConversation?.id]);

  useEffect(() => {
    selectedPhoneNumberIDRef.current = selectedPhoneNumberID;
  }, [selectedPhoneNumberID]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    connectLiveChatSocket();
    const removeListener = addLiveChatSocketListener((data) => {
      const payload = data.payload || {};
      if (data.type === 'whatsapp:conversation:new') {
        fetchConversations(selectedPhoneNumberIDRef.current);
        return;
      }
      if (data.type === 'whatsapp:conversation:update') {
        setConversations((current) =>
          current.map((conversation) =>
            Number(conversation.id) === Number(payload.id) ? { ...conversation, ...payload } : conversation
          )
        );
        if (Number(selectedConversationRef.current?.id || 0) === Number(payload.id || 0)) {
          setSelectedConversation((current) => (current ? { ...current, ...payload } : current));
        }
        return;
      }
      if (data.type !== 'whatsapp:message:new') {
        return;
      }
      const conversationID = Number(payload.conversation_id || 0);
      const selectedConversationID = Number(selectedConversationRef.current?.id || 0);
      const foundConversation = updateConversationPreview(payload);
      if (!foundConversation) {
        fetchConversations(selectedPhoneNumberIDRef.current);
      }
      if (conversationID > 0 && conversationID === selectedConversationID) {
        appendMessage(payload);
        if (payload.sender_type === 'visitor') {
          setConversations((current) =>
            current.map((conversation) =>
              Number(conversation.id) === conversationID ? { ...conversation, unread_count: 0 } : conversation
            )
          );
          HttpServices.Post({
            url: userModuleURL.whatsappConversationRead(conversationID),
            body: {},
            successCallback: () =>
              window.dispatchEvent(new CustomEvent('brand2chat:social-unread-refresh', { detail: { service: 'whatsapp' } })),
            failureCallback: () => {},
          });
        }
      }
    });
    return removeListener;
  }, [appendMessage, fetchConversations, updateConversationPreview]);

  const sendReply = () => {
    const body = reply.trim();
    if (!body || !selectedConversation?.id || sending) return;
    setSending(true);
    HttpServices.Post({
      url: userModuleURL.whatsappConversationMessages(selectedConversation.id),
      body: { message: body },
      successCallback: (data, message) => {
        setReply('');
        appendMessage(data);
        updateConversationPreview(data);
        setSending(false);
        showToast(message || 'WhatsApp message sent', 's');
      },
      failureCallback: (message) => {
        setSending(false);
        showToast(message || 'WhatsApp message could not be sent', 'e');
      },
    });
  };

  const closeConversation = () => {
    if (!selectedConversation?.id) return;
    HttpServices.Post({
      url: userModuleURL.whatsappConversationClose(selectedConversation.id),
      body: {},
      successCallback: () => {
        setSelectedConversation((current) => ({ ...current, status: 'closed' }));
        fetchConversations(selectedPhoneNumberID);
      },
      failureCallback: (message) => showToast(message || 'Failed to close WhatsApp conversation', 'e'),
    });
  };

  const formatTime = (value) => (value ? CommonHelper.formatDateAndTime(value) : '');
  const selectedClosed = selectedConversation?.status === 'closed';

  const getMessageMediaURLs = (message) =>
    (message.media_url || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

  const getMediaPath = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get('path') || parsed.pathname || '';
    } catch (_error) {
      return url || '';
    }
  };

  const isImageURL = (url) => /\.(jpe?g|png|gif|webp)$/i.test(getMediaPath(url));
  const isVideoURL = (url) => /\.(mp4|webm|mov|m4v)$/i.test(getMediaPath(url));
  const isAudioURL = (url) => /\.(mp3|m4a|ogg|oga|opus|aac|wav|webm)$/i.test(getMediaPath(url));

  const getAttachmentName = (url) => {
    const mediaPath = getMediaPath(url);
    const fileName =
      String(mediaPath || '')
        .split('/')
        .pop() || 'Attachment';
    try {
      return decodeURIComponent(fileName);
    } catch (_error) {
      return fileName;
    }
  };

  const renderAttachmentCard = (url) => (
    <Paper
      key={url}
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: { xs: '100%', sm: 260 },
        p: 1,
        borderRadius: 1,
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'rgba(248, 250, 252, 0.95)',
        overflow: 'hidden',
      }}
    >
      <DescriptionOutlinedIcon color="primary" fontSize="small" />
      <Typography
        variant="body2"
        sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {getAttachmentName(url)}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 96px)', minHeight: 620 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={900}>
            WhatsApp Chat
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Select
            size="small"
            value={selectedPhoneNumberID}
            displayEmpty
            onChange={(event) => {
              setSelectedPhoneNumberID(event.target.value);
              setSelectedConversation(null);
              setMessages([]);
            }}
            sx={{ minWidth: 260, bgcolor: 'background.paper' }}
          >
            <MenuItem value="" disabled>
              Select WhatsApp number
            </MenuItem>
            {accounts.map((account) => (
              <MenuItem key={account.id} value={String(account.profile_id)}>
                {account.display_name || 'WhatsApp'} - {account.phone_number || account.profile_id}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      {accountIsSyncing ? (
        <Paper
          variant="outlined"
          sx={{
            height: 'calc(100% - 72px)',
            minHeight: 420,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box>
            <SyncIcon
              sx={{
                fontSize: 56,
                color: '#25D366',
                animation: 'whatsapp-sync-spin 1.4s linear infinite',
                '@keyframes whatsapp-sync-spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
            <Typography variant="h5" fontWeight={900} sx={{ mt: 1 }}>
              Syncing WhatsApp conversations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Imported chats will appear when synchronization is complete.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ height: 'calc(100% - 72px)', minHeight: 0 }}>
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%', minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={800}>Conversations</Typography>
                {selectedAccount && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedAccount.phone_number || selectedAccount.profile_id}
                  </Typography>
                )}
              </Box>
              <Divider />
              {(loadingAccounts || loadingConversations) && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              <List sx={{ overflowY: 'auto', minHeight: 0, flex: 1, p: 0 }}>
                {conversations.map((conversation) => (
                  <ListItemButton
                    key={conversation.id}
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{ alignItems: 'flex-start', py: 1.5 }}
                  >
                    <Avatar sx={{ mr: 1.5, bgcolor: '#dcfce7', color: '#128C7E' }}>
                      <WhatsAppIcon fontSize="small" />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={800} noWrap>
                            {conversation.visitor_name || conversation.visitor_phone || 'WhatsApp customer'}
                          </Typography>
                          {conversation.unread_count > 0 && (
                            <Chip size="small" color="error" label={conversation.unread_count} />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                            {conversation.visitor_phone || conversation.external_thread_id}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.primary" display="block" noWrap>
                            {conversation.last_question || 'No message preview'}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
                {!loadingConversations && conversations.length === 0 && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography fontWeight={800}>No WhatsApp chats</Typography>
                  </Box>
                )}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%', minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              {selectedConversation ? (
                <>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc' }}>
                    <Avatar sx={{ bgcolor: '#dcfce7', color: '#128C7E' }}>
                      <WhatsAppIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" fontWeight={900} noWrap>
                        {selectedConversation.visitor_name || 'WhatsApp customer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {selectedConversation.visitor_phone || selectedConversation.external_thread_id}
                      </Typography>
                    </Box>
                    <Chip color={selectedClosed ? 'error' : 'success'} label={selectedClosed ? 'Closed' : 'Open'} />
                    {!selectedClosed && (
                      <Button color="error" variant="outlined" startIcon={<CloseIcon />} onClick={closeConversation}>
                        Close
                      </Button>
                    )}
                  </Box>
                  <Divider />
                  <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc' }}>
                    {hasEarlierMessages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
                        <Button size="small" variant="outlined" disabled={loadingEarlierMessages} onClick={loadEarlierMessages}>
                          {loadingEarlierMessages ? 'Loading...' : 'Load earlier messages'}
                        </Button>
                      </Box>
                    )}
                    {loadingMessages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={22} />
                      </Box>
                    )}
                    {messages.map((message) => {
                      const isAgent = message.sender_type === 'agent';
                      return (
                        <Box
                          key={message.id}
                          sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', mb: 1.5 }}
                        >
                          <Card
                            variant="outlined"
                            sx={{
                              maxWidth: { xs: '88%', md: '68%' },
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: isAgent ? '#dcfce7' : '#fff',
                              borderColor: isAgent ? '#86efac' : 'divider',
                            }}
                          >
                            <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {message.message}
                            </Typography>
                            {getMessageMediaURLs(message).map((url) => (
                              <Box key={url} sx={{ mt: message.message ? 1 : 0 }}>
                                {message.message_type === 'audio' ||
                                (isAudioURL(url) && message.message_type !== 'video') ? (
                                  <audio
                                    src={url}
                                    controls
                                    preload="metadata"
                                    style={{ maxWidth: 280, width: '100%', display: 'block' }}
                                  />
                                ) : message.message_type === 'video' || isVideoURL(url) ? (
                                  <video
                                    src={url}
                                    controls
                                    style={{ maxWidth: 260, width: '100%', borderRadius: 8, display: 'block' }}
                                  />
                                ) : message.message_type === 'image' ||
                                  message.message_type === 'sticker' ||
                                  isImageURL(url) ? (
                                  <Box
                                    component="img"
                                    src={url}
                                    alt="WhatsApp attachment"
                                    sx={{ maxWidth: 260, width: '100%', borderRadius: 1, display: 'block' }}
                                  />
                                ) : (
                                  renderAttachmentCard(url)
                                )}
                              </Box>
                            ))}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                            >
                              {formatTime(message.created_at)}
                            </Typography>
                          </Card>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                    {selectedClosed ? (
                      <Typography align="center" color="text.secondary">
                        This WhatsApp conversation is closed.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          value={reply}
                          placeholder="Reply on WhatsApp..."
                          onChange={(event) => setReply(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              sendReply();
                            }
                          }}
                        />
                        <IconButton color="primary" onClick={sendReply} disabled={sending || !reply.trim()}>
                          {sending ? <CircularProgress size={22} /> : <SendIcon />}
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                </>
              ) : (
                <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
                  <Box>
                    <WhatsAppIcon sx={{ fontSize: 54, color: '#25D366', mb: 1 }} />
                    <Typography variant="h5" fontWeight={900}>
                      Select a WhatsApp chat
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
